/* eslint-disable max-lines */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import P2 from '@base/P2/P2';
import Link from '@base/Link/Link';
import {
  sendOtpOrEmail,
  login,
  resetOtpResponse,
  authenticate,
  verifyPhone,
  // verifyOtp,
  resetVerifyOtpResponse,
  toggleLoginModal,
  fetchFollowedGoals,
  fetchMyInfo,
  setSelectedGoal,
  showFollowGoalTooltip,
  fetchCountries,
  fetchAccessControl,
  fetchStates
} from '@actions/auth';
import { fetchTopologyInfo } from '@actions/topologies';
import analyticsAction, { segment, sendGAId } from '@actions/analytics';
import cookies from '@utils/cookies';
import pushRoute from '@utils/pushRoute';
import { updateAnalyticsUser } from '@events/sendAnalytics';
import { useSelector, useDispatch } from 'react-redux';
import Me from '@models/Me';
import { Platform, LearnerId, LearnerUserName } from '@events/eventProps';
import { Spacings, TextColors } from '@styles/index';
import {
  LOGIN_SUCCESSFUL,
  LOGIN_OTP_REQUESTED,
  LOGIN_OTP_SUBMITTED
} from '@constants/segment';
import checkWhiteListed from '@utils/whitelistRedirectUrls';
import EnterNumberStep from './EnterNumberStep';
import EnterEmailStep from './EnterEmailStep';
import EnterEmailOtpStep from './EnterEmailOtpStep';
// import AddNumberStep from './AddNumberStep';
import EnterOtpStep from './EnterOtpStep';
import EnterPasswordStep from './EnterPasswordStep';
// import VerifyOtpStep from './VerifyOtpStep';
import Constants from './constants';

const Wrapper = styled.div`
  grid-column: span 10;
  ${({ $source }) => $source === 'page' && 'width: 464px'}
  ${({ theme, $source }) => `${theme.breakpoints.down('tablet')} {
      ${$source === 'page' && 'width: 370px'};
    };`}
  ${({ theme, $source }) => `${theme.breakpoints.down('tablet')} {
      ${$source === 'page' && 'width: 100%'};
    };`}
  ${({ $source }) => $source === 'card' && `padding: ${Spacings.SPACING_8B}`}
`;

const Footer = styled.div`
  grid-column: span 10;
  margin-top: 32px;
  margin-bottom: ${({ $source }) =>
    $source === 'page' ? '0px' : `${Spacings.SPACING_8B}`};
  margin-left: ${({ $source }) =>
    $source === 'card' ? `${Spacings.SPACING_8B}` : 'auto'};
  ${({ theme }) => `${theme.breakpoints.down('tablet')} {
      margin-top: ${Spacings.SPACING_4B};
    };`}
`;

const StyledP2 = styled(P2)`
  padding: 0 ${Spacings.SPACING_1B};
`;

const Span = styled.span`
  padding: 0 4px;
  font-weight: normal;
  font-size: 12px;
  line-height: 150%;
  margin: 0px;
  color: #3c4852;
`;

const FlexP2 = styled(P2)`
  display: flex;
  ${({ theme }) => `${theme.breakpoints.down('tablet')} {
      flex-direction: column;
    };`}
`;

const FlexPragraph = styled.span`
  padding-right: 4px;
`;

const StyledAnchor = styled.a`
  display: inline-block;
`;

// Steps - ENTER_NUMBER_STEP, ENTER_OTP_STEP
// Account Type - ACCOUNT_TYPE_LOGIN, ACCOUNT_TYPE_SIGNUP
// Login Type - LOGIN_TYPE_NUMBER, LOGIN_TYPE_EMAIL

const defaultCountry = {
  code: 'IN',
  name: 'India',
  flag_icon_url: 'https://static.uacdn.net/thumbnail/country-flag-icons/in.png',
  phone_code: '91'
};

const fullStoryId = process.env.FULLSTORY_ID;

const checkIsFollowedGoal = (followedGoals, goalUID, dispatch) => {
  const followedGoalIds = followedGoals.map((item) => item.uid);
  const show = !!(goalUID && !followedGoalIds.includes(goalUID));
  dispatch(showFollowGoalTooltip(show));
};

const initialFieldsData = {
  name: { type: 'name', value: '' },
  email: { type: 'email', value: '' },
  state: { type: 'state', value: '' }
};

const Login = ({
  source,
  showOtpField,
  phoneNumber,
  hideHelp,
  sourceType,
  updateForm,
  handleSourceLogin,
  userInfo,
  loginCallback,
  ttuUserName
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [step, setStep] = useState(Constants.ENTER_NUMBER_STEP);
  const [loginType, setLoginType] = useState(Constants.LOGIN_TYPE_NUMBER);
  const [mobileNumber, setMobileNumber] = useState(phoneNumber);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [fieldsData, setFieldsData] = useState(initialFieldsData);
  const [numberStepErrorMsg, setNumberStepErrorMsg] = useState('');
  const [otpStepError, setOtpStepError] = useState(null);

  const authAccountType = useSelector(({ auth }) => auth.loginType);
  const countries = useSelector(({ auth }) => auth.countries ?? []);
  const states = useSelector(({ auth }) => auth.states);
  const isRegistered = useSelector(
    ({ auth }) => auth.otpOrEmailData?.is_registered ?? false
  );
  const isPhoneVerified = useSelector(
    ({ auth }) => auth.otpOrEmailData?.verify_phone ?? false
  );
  const loggingIn = useSelector(({ auth }) => auth.loggingIn);
  const otpOrEmailResponse = useSelector(({ auth }) => auth.otpOrEmailData);
  const loginMethod = useSelector(
    ({ auth }) => auth.otpOrEmailData?.login_method ?? false
  );
  const { isMobile } = useSelector(({ helper }) => helper);
  const { pathname } = router;
  const isCheckout = /subscribe\/.*/g.test(pathname);
  const getAccountType = () => {
    let type =
      authAccountType === 'login'
        ? Constants.ACCOUNT_TYPE_LOGIN
        : Constants.ACCOUNT_TYPE_SIGNUP;
    if (isCheckout && isMobile) {
      type = Constants.ACCOUNT_TYPE_SIGNUP;
    }
    return type;
  };
  const [accountType, setAccountType] = useState(() => getAccountType());

  const [country, setCountry] = useState(
    countries.length ? countries.find((c) => c.code === 'IN') : defaultCountry
  );

  const clearFormData = () => {
    setFieldsData(initialFieldsData);
    setOtp('');
  };

  const resetErrorMsgs = () => {
    dispatch(resetOtpResponse());
    setNumberStepErrorMsg('');
    setOtpStepError(null);
  };

  useEffect(() => {
    dispatch(fetchCountries());
    dispatch(fetchStates());
    if (mobileNumber) {
      dispatch(sendOtpOrEmail(mobileNumber, 'IN', 1, userInfo.email));
    }
    return () => {
      resetErrorMsgs();
      dispatch(resetVerifyOtpResponse());
    };
  }, []);

  useEffect(() => {
    if (otpOrEmailResponse) {
      const errorMsg = '';
      // if (accountType === Constants.ACCOUNT_TYPE_LOGIN && !isRegistered) {
      //   const subStr =
      //     loginType === Constants.LOGIN_TYPE_EMAIL
      //       ? 'email address'
      //       : 'phone number';
      //   errorMsg = `This ${subStr} is not registered with us.`;
      // }
      // if (accountType === Constants.ACCOUNT_TYPE_SIGNUP && isRegistered) {
      //   errorMsg = 'This phone number is already registered with us.';
      // }

      // have to put check from a flag that which flow is next -> otp from phone, from email or password
      if (errorMsg === '') {
        // if (loginType === Constants.LOGIN_TYPE_EMAIL && !isPhoneVerified)
        //   setStep(Constants.ENTER_PASSWORD_STEP);
        // else setStep(Constants.ENTER_OTP_STEP);
        if (loginType === Constants.LOGIN_TYPE_EMAIL) {
          if (
            loginMethod === 'email_otp' ||
            loginMethod === 'email_phone_otp'
          ) {
            setStep(Constants.ENTER_OTP_STEP);
          } else setStep(Constants.ENTER_PASSWORD_STEP);
        } else setStep(Constants.ENTER_OTP_STEP);
      } else {
        setNumberStepErrorMsg(errorMsg);
      }
    }
  }, [isPhoneVerified, isRegistered, accountType, loginType, loginMethod]);

  const switchAccountType = () => {
    resetErrorMsgs();
    if (accountType === Constants.ACCOUNT_TYPE_LOGIN) {
      setAccountType(Constants.ACCOUNT_TYPE_SIGNUP);
      setLoginType(Constants.LOGIN_TYPE_NUMBER);
      setEmail('');
    } else {
      setAccountType(Constants.ACCOUNT_TYPE_LOGIN);
    }
  };

  const switchLoginType = () => {
    resetErrorMsgs();
    if (loginType === Constants.LOGIN_TYPE_NUMBER) {
      setLoginType(Constants.LOGIN_TYPE_EMAIL);
      setMobileNumber('');
    } else {
      setLoginType(Constants.LOGIN_TYPE_NUMBER);
      setEmail('');
    }
  };

  const changeCountry = (countryOption) => {
    // dispatch(fetchStates(countryOption.value));
    setCountry(countries.find((c) => c.code === countryOption.value));
    // reset mobile number and error msg on change of country
    if (loginType === Constants.LOGIN_TYPE_NUMBER) {
      setMobileNumber('');
      setNumberStepErrorMsg('');
    }
  };

  const submitNumber = () => {
    if (step === Constants.ADD_NUMBER_STEP) {
      dispatch(verifyPhone(mobileNumber, country?.code, 1)).then((response) => {
        if (response.result?.success_code) {
          setStep(Constants.VERIFY_OTP_STEP);
        }
      });
    } else {
      dispatch(sendOtpOrEmail(mobileNumber, country?.code, 1)).then(
        (response = {}) => {
          const { result, error } = response;
          if (error) {
            setNumberStepErrorMsg(error.message);
            return;
          }
          if (!sourceType)
            segment.track(LOGIN_OTP_REQUESTED, {
              is_new_user: !(result?.is_registered ?? true),
              resend: false,
              login_medium:
                loginType === Constants.LOGIN_TYPE_NUMBER ? 'Mobile' : 'Email'
            });
        }
      );
    }
  };

  const submitEmail = () => {
    dispatch(sendOtpOrEmail('', country?.code, 1, email)).then((response) => {
      if (response?.error) {
        setNumberStepErrorMsg(response.error.message);
      }
    });
  };

  const resendOtp = (otp_type) => {
    setOtpStepError(null);
    dispatch(
      sendOtpOrEmail(
        mobileNumber,
        country?.code,
        otp_type,
        loginType === Constants.LOGIN_TYPE_EMAIL ? email : ''
      )
    ).then(({ result }) => {
      if (!sourceType)
        segment.track(LOGIN_OTP_REQUESTED, {
          is_new_user: !result?.is_registered ?? false,
          resend: true,
          login_medium:
            loginType === Constants.LOGIN_TYPE_NUMBER ? 'Mobile' : 'Email'
        });
    });
  };

  const goBackToFirstStep = (event) => {
    dispatch(resetOtpResponse());
    setStep(Constants.ENTER_NUMBER_STEP);
    clearFormData();
    if (updateForm) {
      updateForm();
    }
    event.preventDefault();
  };

  const me = useSelector(({ auth }) => auth.me);
  const campaign = useSelector(({ helper }) => helper.campaign);
  const selectedGoalInfo = useSelector(({ auth }) => auth.selectedGoal);
  const getRedirectionUrl = (selectedGoal) => {
    const { goalSlug, slug, uid } = selectedGoal;
    const href = '/goal/[goalSlug]/[goalUID]';
    const as = `/goal/${goalSlug || slug}/${uid}`;

    const routes = {
      '/': selectedGoal && { href, as },
      '/index': selectedGoal && { href, as },
      '/explore': selectedGoal && { href, as }
    };
    return routes[pathname];
  };
  const postLoginActions = async () => {
    const promises = [
      dispatch(fetchMyInfo()),
      dispatch(
        fetchAccessControl(router.query.goalUID || selectedGoalInfo?.uid)
      )
    ];
    const result = await Promise.all(promises);
    let selectedGoal = null;

    const resp = result[0];
    if (resp?.rawResponse?.uid) {
      const isLoginRoute = pathname === '/login';

      if (isLoginRoute) {
        segment.track(LOGIN_SUCCESSFUL, {
          goal_uid: selectedGoal?.uid,
          goal_name: selectedGoal?.name,
          is_new_user: !isRegistered,
          login_medium:
            loginType === Constants.LOGIN_TYPE_EMAIL ? 'Email' : 'Mobile'
        });

        const redirectQuery = checkWhiteListed(router);

        if (redirectQuery) {
          window.location.href = redirectQuery;
        } else if (cookies.readCookie('loginRoute')) {
          const redirectURL = decodeURIComponent(
            cookies.readCookie('loginRoute')
          );
          cookies.eraseCookie('loginRoute');
          window.location.href = redirectURL;
        } else {
          window.location.href = '/';
        }

        return;
      }

      updateAnalyticsUser(me);

      dispatch(toggleLoginModal(false));

      await dispatch(fetchFollowedGoals()).then((followedGoalsResp) => {
        let selectedGoalUid = null;
        if (followedGoalsResp.result) {
          checkIsFollowedGoal(
            followedGoalsResp.rawResponse,
            router.query.goalUID,
            dispatch
          );

          if (resp?.rawResponse?.plus_subscriptions?.length > 0) {
            const goalItem =
              resp?.rawResponse.plus_subscriptions[0].subscription.value;
            selectedGoalUid = goalItem.uid;
            selectedGoal = {
              ...goalItem,
              goalSlug: goalItem.relative_link.split('/')[2]
            };
          } else if (followedGoalsResp.rawResponse.length > 0) {
            const goalItem = followedGoalsResp.rawResponse[0];
            selectedGoalUid = goalItem.uid;
            selectedGoal = {
              ...goalItem,
              goalSlug: goalItem.relative_link.split('/')[2]
            };
          }
          segment.track(LOGIN_SUCCESSFUL, {
            goal_uid: selectedGoal?.uid,
            goal_name: selectedGoal?.name,
            is_new_user: !isRegistered,
            login_medium:
              loginType === Constants.LOGIN_TYPE_EMAIL ? 'Email' : 'Mobile'
          });
          if (selectedGoalUid) {
            cookies.setCookie('selectedGoalUid', selectedGoalUid);
            dispatch(setSelectedGoal(selectedGoal));
            dispatch(fetchTopologyInfo(selectedGoalUid));

            const redirectionUrl = getRedirectionUrl(selectedGoal);

            if (redirectionUrl && !isMobile) {
              pushRoute(redirectionUrl.href, redirectionUrl.as);
            }
          }
        }
      });
    }
    if (loginCallback) setTimeout(loginCallback, 500);
    const { uid = '', username = '', email: Email = '' } =
      resp.rawResponse ?? {};
    if (uid) {
      window?.analytics?.identify(uid, {
        name: username,
        email: Email,
        userId: uid
      });
      dispatch(sendGAId(username, campaign));
    }

    if (
      resp.rawResponse.uid &&
      resp.rawResponse.is_full_story_user &&
      fullStoryId
    ) {
      import('@fullstory/browser').then((fs) => {
        fs.init({ orgId: fullStoryId });
        fs.identify(resp.rawResponse.uid, {
          [Platform]: 'Desktop',
          [LearnerId]: resp.rawResponse.uid,
          [LearnerUserName]: resp.rawResponse.username,
          email: me?.email,
          selectedGoal: selectedGoalInfo?.uid,
          selectedGoalName: selectedGoalInfo?.name,
          subscriptionDuration: Me.getDaysRemainingInSubscription(
            me?.plusSubscriptions,
            selectedGoalInfo?.uid
          )
        });
      });
    }

    if (!isRegistered) {
      dispatch(analyticsAction('user-register', '', {}, {}));
    }
  };

  const submitLogin = async () => {
    setOtpStepError(null);
    let backend = 'phone';
    if (loginType === Constants.LOGIN_TYPE_EMAIL && otpOrEmailResponse) {
      backend = 'email';
      if (loginMethod === 'email_phone_otp') backend = 'email_phone';
      if (loginMethod === 'email_otp') backend = 'email_otp';
    }
    if (
      step === Constants.ENTER_OTP_STEP &&
      otpOrEmailResponse &&
      !sourceType
    ) {
      segment.track(LOGIN_OTP_SUBMITTED, {
        is_new_user: !isRegistered,
        login_medium:
          loginType === Constants.LOGIN_TYPE_EMAIL ? 'Email' : 'Mobile'
      });
    }
    const data = { email, mobileNumber };
    if (email === '' && fieldsData.email) data.email = fieldsData.email.value;
    if (mobileNumber === '' && fieldsData.phone)
      data.mobileNumber = fieldsData.phone.value;
    if (sourceType === 'marketing' && userInfo.email) {
      data.email = userInfo.email;
      data.mobileNumber = userInfo.phoneNumber;
    }
    const nameValue =
      source === 'ttu' && ttuUserName ? ttuUserName : fieldsData.name?.value;
    const arr = nameValue ? nameValue.split(' ') : [''];
    let firsName = arr[0] ? arr[0] : '';
    let lastName = arr.length > 1 ? arr[1] : '';
    if (userInfo.name) {
      const splitName = userInfo.name.split(' ');
      firsName = splitName?.[0] ?? '';
      lastName = splitName?.[1] ?? '';
    }

    dispatch(
      login(
        data.email,
        data.mobileNumber,
        otp,
        country?.code,
        backend,
        firsName,
        lastName,
        fieldsData.state.value,
        password,
        !isRegistered
      )
    ).then((response) => {
      const loginResponse = response.result;
      if (
        sourceType === 'marketing' &&
        handleSourceLogin &&
        response.statusCode === 400
      ) {
        handleSourceLogin(response);
      }
      if (loginResponse?.access_token) {
        dispatch(
          authenticate({
            accessToken: loginResponse.access_token,
            refreshToken: loginResponse.refresh_token
          })
        );
        cookies.eraseCookie('anonymous_session_id');
        cookies.setCookie(
          'accessToken',
          loginResponse.access_token,
          3024000,
          true,
          source === 'iframe'
        );
        cookies.setCookie(
          'refreshToken',
          loginResponse.refresh_token,
          3024000,
          true,
          source === 'iframe'
        );
        if (sourceType === 'marketing' && handleSourceLogin) {
          return handleSourceLogin(loginResponse);
        }
        cookies.eraseCookie('selectedGoalUid');
        postLoginActions();
      } else if (response.error?.error_code) {
        setOtpStepError({
          message: response.error.message,
          code: response.error.error_code
        });
      } else {
        // fallback for unknown error codes
        dispatch({
          type: 'SET_SNACKBAR_MESSAGE',
          message: response.error.message || '',
          time: 5000,
          messageType: 'error'
        });
      }
      // if (loginResponse?.access_token && loginResponse.user_info) {
      //   dispatch(authenticate(loginResponse.access_token));
      //   if (loginResponse.user_info.is_phone_verified) {
      //     cookies.setCookie('accessToken', loginResponse.access_token);
      //     cookies.eraseCookie('selectedGoalUid');
      //     postLoginActions();
      //   } else {
      //     if (
      //       loginResponse.user_info.phone &&
      //       loginResponse.user_info.phone !== ''
      //     ) {
      //       setMobileNumber(loginResponse.user_info.phone);
      //     }
      //     setStep(Constants.ADD_NUMBER_STEP);
      //   }
      // }
    });
  };

  const renderFirstStep = () => {
    if (
      accountType === Constants.ACCOUNT_TYPE_LOGIN &&
      loginType === Constants.LOGIN_TYPE_EMAIL
    ) {
      return (
        <EnterEmailStep
          changeAccountType={switchAccountType}
          email={email}
          setEmail={setEmail}
          submitEmail={submitEmail}
          switchLoginType={switchLoginType}
          errorMsg={numberStepErrorMsg}
        />
      );
    }
    return (
      <EnterNumberStep
        sourceType={sourceType}
        accountType={accountType}
        changeAccountType={switchAccountType}
        changeCountry={changeCountry}
        country={country}
        countries={countries}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
        submitNumber={submitNumber}
        switchLoginType={switchLoginType}
        errorMsg={numberStepErrorMsg}
        isTTU={source === 'ttu'}
      />
    );
  };

  if (!countries.length) return null;
  return (
    <>
      <Wrapper $source={source}>
        {step === Constants.ENTER_NUMBER_STEP && !showOtpField
          ? renderFirstStep()
          : null}
        {(step === Constants.ENTER_OTP_STEP && loginMethod !== 'email_otp') ||
        showOtpField ? (
          <EnterOtpStep
            accountType={accountType}
            mobileNumber={mobileNumber}
            changeCountry={changeCountry}
            country={country}
            countries={countries}
            resendOtp={resendOtp}
            editMobileNumber={goBackToFirstStep}
            otp={otp}
            setOtp={setOtp}
            fieldsData={fieldsData}
            setFieldsData={setFieldsData}
            otpOrEmailResponse={otpOrEmailResponse}
            submitLogin={submitLogin}
            loggingIn={loggingIn}
            states={states}
            error={otpStepError}
            sourceType={sourceType}
            isTTU={source === 'ttu'}
            ttuUserName={ttuUserName}
          />
        ) : null}
        {step === Constants.ENTER_OTP_STEP && loginMethod === 'email_otp' ? (
          <EnterEmailOtpStep
            accountType={accountType}
            email={email}
            editEmail={goBackToFirstStep}
            changeCountry={changeCountry}
            country={country}
            countries={countries}
            resendOtp={resendOtp}
            editMobileNumber={goBackToFirstStep}
            otp={otp}
            setOtp={setOtp}
            fieldsData={fieldsData}
            setFieldsData={setFieldsData}
            otpOrEmailResponse={otpOrEmailResponse}
            submitLogin={submitLogin}
            loggingIn={loggingIn}
            states={states}
            error={otpStepError}
          />
        ) : null}
        {step === Constants.ENTER_PASSWORD_STEP ? (
          <EnterPasswordStep
            email={email}
            editEmail={goBackToFirstStep}
            password={password}
            setPassword={setPassword}
            country={country}
            fieldsData={fieldsData}
            setFieldsData={setFieldsData}
            otpOrEmailResponse={otpOrEmailResponse}
            submitLogin={submitLogin}
            loggingIn={loggingIn}
            states={states}
            error={otpStepError}
          />
        ) : null}
        {/* step === Constants.ADD_NUMBER_STEP ? (
        <AddNumberStep
          changeCountry={changeCountry}
          country={country}
          countries={countries}
          mobileNumber={mobileNumber}
          setMobileNumber={setMobileNumber}
          submitNumber={submitNumber}
          switchLoginType={switchLoginType}
          verifyOtpResponse={verifyOtpResponse}
        />
      ) : null */}
        {/* step === Constants.VERIFY_OTP_STEP ? (
        <VerifyOtpStep
          mobileNumber={mobileNumber}
          changeCountry={changeCountry}
          country={country}
          countries={countries}
          resendOtp={resendOtp}
          editMobileNumber={goBackToAddNumberStep}
          otp={otp}
          setOtp={setOtp}
          verifyOtpResponse={verifyOtpResponse}
          submitOtpVerification={submitOtpVerification}
        />
      ) : null */}
      </Wrapper>

      {accountType === Constants.ACCOUNT_TYPE_SIGNUP ? (
        <Footer $source={source}>
          <FlexP2 color={TextColors.SECONDARY} source={source}>
            <FlexPragraph>By signing up,</FlexPragraph>
            <FlexPragraph source={source}>
              you agree to
              <Link href="/terms" as="/terms" newTab>
                <Span>Terms & Conditions</Span>
              </Link>
              and
              <Link href="/privacy" as="/privacy" newTab>
                <Span>Privacy Policy</Span>
              </Link>
            </FlexPragraph>
          </FlexP2>
        </Footer>
      ) : null}
      {accountType === Constants.ACCOUNT_TYPE_LOGIN && !hideHelp ? (
        <Footer $source={source}>
          <FlexP2 color={TextColors.SECONDARY} source={source}>
            <FlexPragraph>Having trouble?</FlexPragraph>
            <FlexPragraph>
              Please contact
              <StyledAnchor href="mailTo:help@unacademy.com">
                <StyledP2>help@unacademy.com</StyledP2>
              </StyledAnchor>
              for further support.
            </FlexPragraph>
          </FlexP2>
        </Footer>
      ) : null}
    </>
  );
};

export default Login;

Login.propTypes = {
  // verifyOtpResponse: PropTypes.objectOf(PropTypes.any),
  source: PropTypes.string,
  showOtpField: PropTypes.bool,
  phoneNumber: PropTypes.string,
  hideHelp: PropTypes.bool,
  sourceType: PropTypes.string,
  updateForm: PropTypes.oneOfType([PropTypes.func, PropTypes.any]),
  handleSourceLogin: PropTypes.oneOfType([PropTypes.func, PropTypes.any]),
  userInfo: PropTypes.objectOf(PropTypes.any),
  loginCallback: PropTypes.func,
  ttuUserName: PropTypes.string
};

Login.defaultProps = {
  // verifyOtpResponse: null,
  source: 'drawer',
  showOtpField: false,
  phoneNumber: '',
  hideHelp: false,
  sourceType: '',
  updateForm: null,
  handleSourceLogin: null,
  userInfo: {},
  loginCallback: null,
  ttuUserName: ''
};
